/**
 * Wave 8 — Portfolio Command Center KPIs from commercial dashboard.
 * Deterministic buckets only — no invented win rates / %.
 */
import type { EpcCommercialDashboard } from '@/services/epcCommercial.service';

export type PortfolioUrgency = 'critical' | 'attention' | 'neutral';

export type PortfolioKpi = {
  id: string;
  label: string;
  count: number;
  urgency: PortfolioUrgency;
  href: string;
  detail?: string;
};

export type PortfolioAttentionItem = {
  id: string;
  severity: 'critical' | 'warning' | 'info';
  title: string;
  detail?: string;
  href: string;
};

export type CommandCenterPortfolio = {
  kpis: PortfolioKpi[];
  attention: PortfolioAttentionItem[];
  totals: {
    tenders: number;
    awards: number;
    contracts: number;
    mobilizations: number;
    openRisks: number;
  };
};

function sumCounts(rows?: Array<{ status: string; cnt: number | string }>): number {
  if (!rows?.length) return 0;
  return rows.reduce((acc, r) => acc + Number(r.cnt || 0), 0);
}

function countStatus(
  rows: Array<{ status: string; cnt: number | string }> | undefined,
  match: (s: string) => boolean
): number {
  if (!rows?.length) return 0;
  return rows
    .filter((r) => match(String(r.status || '')))
    .reduce((a, r) => a + Number(r.cnt || 0), 0);
}

export function buildCommandCenterPortfolio(
  d: EpcCommercialDashboard | null | undefined
): CommandCenterPortfolio {
  const tenders = sumCounts(d?.tendersByStatus);
  const tendersDraft = countStatus(d?.tendersByStatus, (s) => /draft/i.test(s));
  const tendersSubmitted = countStatus(d?.tendersByStatus, (s) =>
    /submit|pending|in.?progress|active/i.test(s)
  );
  const awards = sumCounts(d?.awardsByStatus);
  const awardsPending = countStatus(d?.awardsByStatus, (s) =>
    /submit|pending|draft|loi/i.test(s)
  );
  const contracts = sumCounts(d?.contractsByStatus);
  const contractsActive = countStatus(d?.contractsByStatus, (s) => /active/i.test(s));
  const mobilizations = sumCounts(d?.mobilizationsByStatus);
  const mobPending = countStatus(
    d?.mobilizationsByStatus,
    (s) => !/activated|closed|cancelled/i.test(s)
  );
  const openRisks = Number(d?.openRiskCount || 0);

  const kpis: PortfolioKpi[] = [
    {
      id: 'leads-register',
      label: 'Leads (CRM)',
      count: 0,
      urgency: 'neutral',
      href: '/growth/leads',
      detail: 'Early pursuit signals',
    },
    {
      id: 'opportunities-register',
      label: 'Opportunities (CRM)',
      count: 0,
      urgency: 'neutral',
      href: '/growth/opportunities',
      detail: 'Weighted pipeline in opportunity workspace',
    },
    {
      id: 'prequal-register',
      label: 'Prequalifications',
      count: 0,
      urgency: 'attention',
      href: '/epc/commercial/prequalifications',
      detail: 'PQ due / blockers in register',
    },
    {
      id: 'bid-decisions',
      label: 'Bid decisions',
      count: 0,
      urgency: 'attention',
      href: '/epc/commercial/bid-decisions',
      detail: 'UX4 bid / no-bid register',
    },
    {
      id: 'tenders-open',
      label: 'Tenders in flight',
      count: tendersDraft + tendersSubmitted > 0 ? tendersDraft + tendersSubmitted : tenders,
      urgency: tendersDraft > 0 ? 'attention' : 'neutral',
      href: '/epc/commercial/tenders',
      detail: tendersDraft ? `${tendersDraft} draft` : undefined,
    },
    {
      id: 'awards-pending',
      label: 'Awards pending',
      count: awardsPending,
      urgency: awardsPending > 0 ? 'attention' : 'neutral',
      href: '/epc/commercial/awards',
    },
    {
      id: 'contracts-active',
      label: 'Active contracts',
      count: contractsActive,
      urgency: 'neutral',
      href: '/epc/commercial/contracts',
    },
    {
      id: 'mobilizations-open',
      label: 'Mobilizations open',
      count: mobPending,
      urgency: mobPending > 0 ? 'attention' : 'neutral',
      href: '/epc/commercial/mobilizations',
    },
    {
      id: 'open-risks',
      label: 'Open tender risks',
      count: openRisks,
      urgency: openRisks > 0 ? 'critical' : 'neutral',
      href: '/epc/commercial/control-tower',
    },
  ];

  const attention: PortfolioAttentionItem[] = [];
  if (openRisks > 0) {
    attention.push({
      id: 'open-risks',
      severity: 'critical',
      title: `${openRisks} open tender risk(s)`,
      detail: 'Review tender risks before bid / submit / award decisions',
      href: '/epc/commercial/tenders',
    });
  }
  if (awardsPending > 0) {
    attention.push({
      id: 'awards-pending',
      severity: 'warning',
      title: `${awardsPending} award(s) in flight`,
      detail: 'Reconcile and convert via award cockpit',
      href: '/epc/commercial/awards',
    });
  }
  if (mobPending > 0) {
    attention.push({
      id: 'mob-pending',
      severity: 'warning',
      title: `${mobPending} mobilization(s) not activated`,
      detail: 'Complete checklist → Approval → activate project',
      href: '/epc/commercial/mobilizations',
    });
  }
  if (tendersDraft > 0) {
    attention.push({
      id: 'tenders-draft',
      severity: 'info',
      title: `${tendersDraft} draft tender(s)`,
      href: '/epc/commercial/tenders',
    });
  }

  return {
    kpis,
    attention,
    totals: { tenders, awards, contracts, mobilizations, openRisks },
  };
}
